import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Settings, Save, Sliders } from 'lucide-react';

const DemoSettings: React.FC = () => {
  const [thresholds, setThresholds] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');

  const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/demo`;

  const sensorLabels: Record<string, string> = {
    soilMoisture: 'Humidité Sol',
    temperature: 'Température',
    humidity: 'Humidité Air',
    soilPH: 'pH Sol',
    soilEC: 'Conductivité Sol',
    light: 'Luminosité',
    leafWetness: 'Humidité Feuilles',
    co2: 'CO2',
    waterPressure: 'Pression Eau',
    waterFlow: 'Débit Eau'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/settings/thresholds`);
      if (response.ok) {
        const data = await response.json();
        setThresholds(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    toast.success('Paramètres enregistrés avec succès');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-xl shadow-2xl p-6 sm:p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="h-10 w-10" />
            <div>
              <h1 className="text-3xl font-bold">Paramètres</h1>
              <p className="text-indigo-50 text-lg">Configuration des seuils et alertes</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-bold flex items-center gap-2 shadow-lg"
          >
            <Save className="h-5 w-5" />
            <span>Enregistrer</span>
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <Sliders className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          <span>Seuils d Alerte</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(thresholds).map(([sensor, values]: [string, any]) => (
            <div key={sensor} className="p-5 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">
                {sensorLabels[sensor] || sensor}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Minimum
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={values.min}
                      onChange={(e) => {
                        const newThresholds = { ...thresholds };
                        newThresholds[sensor] = { ...values, min: parseFloat(e.target.value) };
                        setThresholds(newThresholds);
                      }}
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold"
                      step="0.1"
                    />
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 min-w-[60px]">
                      {values.unit}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Maximum
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={values.max}
                      onChange={(e) => {
                        const newThresholds = { ...thresholds };
                        newThresholds[sensor] = { ...values, max: parseFloat(e.target.value) };
                        setThresholds(newThresholds);
                      }}
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold"
                      step="0.1"
                    />
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 min-w-[60px]">
                      {values.unit}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemoSettings;
